import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import FixedGenreGrid from "../components/FixedGenreGrid";
import "./MangaGenrePage.css";

const MangaGenrePage = () => {
  const { genre } = useParams();
  const [mangaList, setMangaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalManga, setTotalManga] = useState(0);
  const [layout, setLayout] = useState("horizontal");
  const [sortOrder, setSortOrder] = useState("score"); // Default sort by score
  const [itemsPerPage, setItemsPerPage] = useState(12); // Show 12 manga per page

  // Decode the genre parameter from the URL
  const decodedGenre = decodeURIComponent(genre);

  // Detect screen size and set appropriate card layout
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setLayout("vertical");
      } else {
        setLayout("horizontal");
      }
    };

    // Initial check
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch manga for the specific genre
  useEffect(() => {
    const fetchMangaByGenre = async () => {
      setLoading(true);
      setError(null);

      try {
        const API_BASE =
          process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";
          
        // Build URL with query parameters
        const url = new URL(`${API_BASE}/api/manga/genre/${encodeURIComponent(decodedGenre)}`);
        url.searchParams.append('page', currentPage);
        url.searchParams.append('limit', itemsPerPage);
        url.searchParams.append('sort', sortOrder);
        
        console.log(`Fetching: ${url.toString()}`);
        
        const response = await fetch(url.toString());

        if (!response.ok) {
          throw new Error(`Failed to fetch ${decodedGenre} manga (${response.status})`);
        }

        const responseData = await response.json();
        
        // Check if the response is in the new paginated format
        if (responseData.data && responseData.pagination) {
          console.log(`Fetched ${responseData.data.length} manga for genre: ${decodedGenre} (page ${responseData.pagination.page} of ${responseData.pagination.pages})`);
          
          // Set the manga list from the data property
          setMangaList(responseData.data);
          
          // Set pagination information
          setCurrentPage(responseData.pagination.page);
          setTotalPages(responseData.pagination.pages);
          setTotalManga(responseData.pagination.total);
        } else {
          // Handle the old format (direct array) for backward compatibility
          console.log(`Fetched ${responseData.length} manga for genre: ${decodedGenre} (old format)`);
          setMangaList(responseData);
          setTotalPages(Math.ceil(responseData.length / itemsPerPage));
          setTotalManga(responseData.length);
        }
      } catch (error) {
        console.error(`Error fetching ${decodedGenre} manga:`, error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMangaByGenre();
  }, [decodedGenre, currentPage, itemsPerPage, sortOrder]);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Scroll to top when changing pages
    window.scrollTo(0, 0);
  };
  
  // Handle sort change
  const handleSortChange = (e) => {
    setSortOrder(e.target.value);
    setCurrentPage(1); // Reset to first page on sort change
  };
  
  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Loading state
  if (loading) {
    return (
      <div className="manga-genre-page">
        <div className="manga-genre-page-title-container">
          <Link to="/manga/genres" className="back-button">
            ← All Genres
          </Link>
          <h1 className="manga-genre-page-title">{decodedGenre} Manga</h1>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading {decodedGenre} manga...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="manga-genre-page">
        <div className="manga-genre-page-title-container">
          <Link to="/manga/genres" className="back-button">
            ← All Genres
          </Link>
          <h1 className="manga-genre-page-title">{decodedGenre} Manga</h1>
        </div>
        <div className="error-container">
          <h3>Failed to load {decodedGenre} manga</h3>
          <p>{error}</p>
          <Link to="/manga/genres" className="back-button">
            Return to All Genres
          </Link>
        </div>
      </div>
    );
  }

  // No results state
  if (!mangaList || mangaList.length === 0) {
    return (
      <div className="manga-genre-page">
        <div className="manga-genre-page-title-container">
          <Link to="/manga/genres" className="back-button">
            ← All Genres
          </Link>
          <h1 className="manga-genre-page-title">{decodedGenre} Manga</h1>
        </div>
        <div className="no-results">
          <p>No manga found for this genre. Please try another genre.</p>
          <Link to="/manga/genres" className="back-button">
            Browse Other Genres
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="manga-genre-page">
      <div className="manga-genre-page-title-container">
        <Link to="/manga/genres" className="back-button">
          ← All Genres
        </Link>
        <h1 className="manga-genre-page-title">{decodedGenre} Manga</h1>
        <p className="manga-genre-page-subtitle">Showing {mangaList.length} of {totalManga} manga in this genre</p>
      </div>
      
      {/* Sort and Filter Controls */}
      <div className="genre-controls">
        <div className="genre-filter">
          <label htmlFor="sort-select">Sort by:</label>
          <select
            id="sort-select"
            value={sortOrder}
            onChange={handleSortChange}
            className="sort-select"
          >
            <option value="score">Score (Highest First)</option>
            <option value="title">Title (A-Z)</option>
            <option value="popularity">Popularity</option>
            <option value="published.from">Release Date (Newest First)</option>
          </select>
          
          <label htmlFor="limit-select" style={{ marginLeft: '15px' }}>Show:</label>
          <select
            id="limit-select"
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            className="limit-select"
          >
            <option value={12}>12 per page</option>
            <option value={24}>24 per page</option>
            <option value={48}>48 per page</option>
          </select>
        </div>
        
        <div className="pagination-info">
          Page {currentPage} of {totalPages}
        </div>
      </div>

      {/* Use MangaCard components with fixed height wrappers */}
      <FixedGenreGrid items={mangaList} layout={layout} type="manga" />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="page-btn prev"
          >
            Previous
          </button>

          <div className="page-numbers">
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(
                (num) =>
                  num === 1 ||
                  num === totalPages ||
                  (num >= currentPage - 1 && num <= currentPage + 1)
              )
              .map((num, i, arr) => (
                <React.Fragment key={num}>
                  {i > 0 && arr[i - 1] !== num - 1 && (
                    <span className="page-ellipsis">•••</span>
                  )}
                  <button
                    onClick={() => handlePageChange(num)}
                    className={`page-num ${currentPage === num ? "active" : ""}`}
                  >
                    {num}
                  </button>
                </React.Fragment>
              ))}
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="page-btn next"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default MangaGenrePage;